import { EventEmitter } from "events";
import axios from "axios";
import config from "../config/index";
import { useDispatch, useSelector } from "react-redux";
import {
  setAccountLinkageLive,
  setEmailFlowSettings,
  setLaundryPlaceId,
  setShopId,
  setShopName,
  setShopDeliveryFee,
  clearState,
} from "../store/slices/cartSlice";
import { clearLayoutState } from "../store/slices/layoutSlice";
import {
  saveAuthTokens,
  getAuthTokens,
  clearAuthTokens,
} from "./sessionTokens";

// withCredentials is kept on so that when the backend finally ships the
// HttpOnly-cookie auth flow (per audit comment below), cookies are sent
// automatically. Until then we also forward Devise Token Auth headers via
// the interceptors below — see sessionTokens.js for the rationale.
//
// BACKEND REQUIREMENT (still pending): Rails API must respond to
//   POST /auth/sign_in with Set-Cookie: access-token=…; HttpOnly; Secure;
//   SameSite=Lax and validate that cookie on every subsequent request.
//   When that ships, the interceptor + sessionTokens.js module can be deleted.
axios.defaults.withCredentials = true;

// On every outgoing request: attach Devise Token Auth headers if we have them.
axios.interceptors.request.use((requestConfig) => {
  const tokens = getAuthTokens();
  if (tokens) {
    requestConfig.headers["access-token"] = tokens["access-token"];
    requestConfig.headers["client"] = tokens["client"];
    requestConfig.headers["uid"] = tokens["uid"];
  }
  return requestConfig;
});

// On every successful response: capture refreshed Devise tokens. Devise
// rotates access-token on each request; if we don't capture the new one
// here, the next request fails with 401.
axios.interceptors.response.use((response) => {
  if (response?.headers?.["access-token"]) {
    saveAuthTokens(response.headers);
  }
  return response;
});

const eventEmitter = new EventEmitter();

const useAuthentication = () => {
  const dispatch = useDispatch();
  const shopId = useSelector((state) => state.cart?.userInfo?.shopId);

  const userLogin = async (email, password) => {
    try {
      const response = await axios.post(`${config.ENDPOINT_AUTH}/sign_in`, {
        email: email,
        password: password,
      });

      // Capture Devise Token Auth headers from the login response (the
      // response interceptor handles refresh, but the initial save still
      // matters because the very first call after login goes through here).
      saveAuthTokens(response.headers);

      dispatch(setShopId(response.data.data?.id));
      dispatch(setShopName(response.data.data?.name_shop));
      dispatch(setShopDeliveryFee(response.data.data?.delivery_fee));
      dispatch(setLaundryPlaceId(response.data.data?.laundry_place_id));
      dispatch(setAccountLinkageLive(response.data.data?.account_linkage_live));
      dispatch(setEmailFlowSettings(response.data.data?.email_flow_settings));

      eventEmitter.emit("loginSuccess", response.data.data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message:
          error?.response?.data?.errors?.[0] ??
          "Something went wrong. Please try again later!",
      };
    }
  };

  const userLogout = async (navigate) => {
    try {
      await axios.delete(`${config.ENDPOINT_AUTH}/sign_out`);
    } catch (_) {
      // best-effort — clear session on client regardless
    } finally {
      clearAuthTokens();
      dispatch(clearState());
      dispatch(clearLayoutState());
      navigate("/user/login");
      eventEmitter.emit("logoutSuccess");
    }
  };

  // Redux-persisted shopId survives tab close (localStorage). Devise tokens
  // live in sessionStorage and clear when the tab closes. Require both —
  // otherwise a stale shopId without tokens silently sends unauthenticated
  // requests and the user appears logged in until the first 401.
  const isAuthenticated = () => !!shopId && !!getAuthTokens();

  return { userLogin, userLogout, isAuthenticated };
};

export default useAuthentication;
